/**
 * Correspondance de routes FR↔EN — source de vérité du sélecteur de langue
 * et de la localisation des liens du header/footer.
 *
 * Stratégie "as-needed" : le FR garde ses URLs d'origine, l'EN vit sous `/en`.
 * La plupart des pages ne font qu'ajouter le préfixe `/en`, mais plusieurs ont
 * des slugs DIFFÉRENTS (pages légales, location secondaire, slugs chauffeur /
 * transferts / partenaires). Ce module mappe n'importe quel chemin vers son
 * équivalent dans l'autre langue pour que le switcher ne tombe jamais sur un
 * 404. Les pages sans équivalent retombent sur l'accueil de la langue cible.
 *
 * Volontairement gardé en map de chaînes autonome (sans import de données
 * lourdes) → sûr dans le bundle client. Reflète, en restant synchronisé :
 *   - chauffeur-en.ts        → CHAUFFEUR_SLUG_MAP
 *   - data/transfers         → routes.ts / routesEn.ts (ordre positionnel)
 *   - data/partners          → segments.ts (slug / enSlug)
 *   - location *Content.ts   → canoniques FR/EN
 *   - excursions             → slugs miroir 1:1 (préfixe seulement)
 *   - vehicules              → mêmes ids (préfixe seulement)
 */
import {
  type Locale,
  localeFromPath,
  FULLY_ROLLED_OUT_LOCALES,
} from "@/config/i18n";

/** FR canonique (sans préfixe) → EN canonique (avec `/en`). */
const FR_TO_EN: Record<string, string> = {
  "/": "/en",

  // Silo chauffeur (VTC)
  "/chauffeur": "/en/chauffeur",
  "/chauffeur/services": "/en/chauffeur/services",
  "/chauffeur/cannes": "/en/chauffeur/cannes",
  "/chauffeur/cote-d-azur": "/en/chauffeur/french-riviera",
  "/chauffeur/evenements": "/en/chauffeur/events",
  "/chauffeur/entreprise": "/en/chauffeur/business",
  "/chauffeur/aeroport": "/en/chauffeur/airport",
  "/chauffeur/nice": "/en/chauffeur/nice",
  "/chauffeur/monaco": "/en/chauffeur/monaco",
  "/chauffeur/antibes": "/en/chauffeur/antibes",
  "/chauffeur/saint-tropez": "/en/chauffeur/saint-tropez",
  "/chauffeur/festival-de-cannes": "/en/chauffeur/cannes-film-festival",
  "/chauffeur/mipim": "/en/chauffeur/mipim",
  "/chauffeur/mipcom": "/en/chauffeur/mipcom",
  "/chauffeur/cannes-lions": "/en/chauffeur/cannes-lions",
  "/chauffeur/grand-prix-de-monaco": "/en/chauffeur/monaco-grand-prix",
  "/chauffeur/monaco-yacht-show": "/en/chauffeur/monaco-yacht-show",
  "/chauffeur/saint-jean-cap-ferrat": "/en/chauffeur/saint-jean-cap-ferrat",
  "/chauffeur/eze": "/en/chauffeur/eze",
  "/chauffeur/villefranche-sur-mer": "/en/chauffeur/villefranche-sur-mer",
  "/chauffeur/mougins": "/en/chauffeur/mougins",
  "/chauffeur/sophia-antipolis": "/en/chauffeur/sophia-antipolis",
  "/chauffeur/cannes-yachting-festival":
    "/en/chauffeur/cannes-yachting-festival",
  "/chauffeur/tfwa-cannes": "/en/chauffeur/tfwa-cannes",
  "/chauffeur/monte-carlo-masters": "/en/chauffeur/monte-carlo-masters",
  "/chauffeur/iltm-cannes": "/en/chauffeur/iltm-cannes",
  "/chauffeur/carnaval-de-nice": "/en/chauffeur/nice-carnival",
  "/chauffeur/voiture-mariage-cannes": "/en/chauffeur/wedding-car-cannes",

  // Blog — hub ; les fiches /blog/<slug> sont gérées par préfixe (MIRROR_PREFIXES)
  "/blog": "/en/blog",

  // Agenda des événements Riviera — slug FR≠EN. L'absence de cette entrée
  // faisait retomber le sélecteur de langue sur l'accueil depuis /evenements,
  // et empêchait le plan du site de dériver les liens EN/AR (les deux pages
  // restaient orphelines). Le hreflang, lui, était déjà correct : il vient du
  // metadataRouter, pas d'ici.
  "/evenements": "/en/events",

  // Transferts (slugs FR≠EN, appariés positionnellement)
  "/chauffeur/transferts": "/en/chauffeur/transfers",
  "/chauffeur/transferts/cannes-aeroport-nice":
    "/en/chauffeur/transfers/cannes-nice-airport",
  "/chauffeur/transferts/monaco-aeroport-nice":
    "/en/chauffeur/transfers/monaco-nice-airport",
  "/chauffeur/transferts/aeroport-nice-saint-tropez":
    "/en/chauffeur/transfers/nice-airport-saint-tropez",
  "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat":
    "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
  "/chauffeur/transferts/monaco-saint-tropez":
    "/en/chauffeur/transfers/monaco-saint-tropez",
  "/chauffeur/transferts/cannes-monaco":
    "/en/chauffeur/transfers/cannes-monaco",
  "/chauffeur/transferts/cannes-saint-tropez":
    "/en/chauffeur/transfers/cannes-saint-tropez",

  // Partenaires (conciergerie≠concierge)
  "/chauffeur/partenaires": "/en/chauffeur/partners",
  "/chauffeur/partenaires/conciergerie": "/en/chauffeur/partners/concierge",
  "/chauffeur/partenaires/hotel": "/en/chauffeur/partners/hotel",
  "/chauffeur/partenaires/agence-evenementielle":
    "/en/chauffeur/partners/event-agency",
  "/chauffeur/partenaires/yachting": "/en/chauffeur/partners/yachting",
  "/chauffeur/partenaires/wedding-planner":
    "/en/chauffeur/partners/wedding-planner",

  // Silo excursions (hub ; les fiches /excursions/<slug> sont gérées par préfixe)
  "/excursions": "/en/excursions",

  // Silo location
  "/location": "/en/location",
  "/location/vehicules": "/en/location/vehicules",
  "/location/courte-duree": "/en/location/short-term-car-rental",
  "/location/avec-chauffeur": "/en/location/car-with-driver",
  "/location/cabriolet": "/en/location/convertible-car-rental",
  "/location/suv": "/en/location/suv-rental",
  "/location/mercedes": "/en/location/mercedes-car-rental",
  "/location/audi": "/en/location/audi-car-rental",
  "/location/porsche": "/en/location/porsche-car-rental",
  "/location/lamborghini": "/en/location/lamborghini-car-rental",
  "/location/longue-duree": "/en/location/long-term-car-rental",
  "/location/offres": "/en/location/rental-offers",
  "/location/location-voiture-cannes": "/en/location/car-rental-cannes",
  "/location/location-voiture-nice": "/en/location/car-rental-nice",
  "/location/location-voiture-monaco": "/en/location/car-rental-monaco",
  "/location/location-voiture-antibes": "/en/location/car-rental-antibes",
  "/location/location-voiture-saint-tropez":
    "/en/location/car-rental-saint-tropez",
  "/location/prestige": "/en/location/prestige-car-rental",

  // Conversion + infos
  "/reservation": "/en/reservation",
  "/contact": "/en/contact",
  "/faq": "/en/faq",
  "/a-propos": "/en/about",

  // Légal + plan du site
  "/cgv": "/en/terms",
  "/cgu": "/en/terms-of-use",
  "/mentions-legales": "/en/legal-notice",
  "/politique-de-confidentialite": "/en/privacy-policy",
  "/plan-du-site": "/en/sitemap",
};

const EN_TO_FR: Record<string, string> = Object.fromEntries(
  Object.entries(FR_TO_EN).map(([fr, en]) => [en, fr]),
);

/**
 * FR canonique → AR canonique (avec `/ar`). Déploiement INCRÉMENTAL : on
 * n'ajoute une entrée QUE lorsque la page arabe existe réellement. Tant qu'une
 * route FR n'est pas ici, l'arabe n'est ni proposé dans le switcher ni émis en
 * hreflang pour elle (localeHasPage), donc jamais de 404. Contrairement à l'EN,
 * PAS de raccourci "mirror" pour l'arabe : tout jumeau AR est explicite.
 */
// L'arabe couvre désormais TOUT l'arbre (mirror de l'EN) : on génère FR_TO_AR
// depuis les clés FR de FR_TO_EN — chaque route FR ayant un jumeau EN a aussi
// un jumeau AR `/ar<route>` (mêmes slugs latins que le FR, sous le préfixe /ar).
const FR_TO_AR: Record<string, string> = Object.fromEntries(
  Object.keys(FR_TO_EN).map((fr) => [fr, fr === "/" ? "/ar" : `/ar${fr}`]),
);

const AR_TO_FR: Record<string, string> = Object.fromEntries(
  Object.entries(FR_TO_AR).map(([fr, ar]) => [ar, fr]),
);

/** Préfixes dont les slugs sont identiques FR↔EN (préfixe `/en` seulement). */
const MIRROR_PREFIXES = ["/location/vehicules/", "/excursions/", "/blog/"];

/** Ramène n'importe quel chemin (fr/en/ar) à sa base FR canonique. */
const toFrCanonical = (path: string): string => {
  const src = localeFromPath(path);
  if (src === "fr") return path;
  if (src === "en") {
    if (EN_TO_FR[path]) return EN_TO_FR[path];
    for (const prefix of MIRROR_PREFIXES) {
      if (path.startsWith(`/en${prefix}`)) return path.slice(3);
    }
    return "/";
  }
  // src === "ar"
  return AR_TO_FR[path] ?? "/";
};

/** Normalise : garantit un `/` initial, retire le `/` final (sauf racine). */
const normalizePath = (pathname: string): string => {
  if (!pathname) return "/";
  let p = pathname.split("#")[0].split("?")[0];
  if (!p.startsWith("/")) p = `/${p}`;
  if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");
  return p || "/";
};

/**
 * Équivalent d'un chemin dans la langue cible, pour le SÉLECTEUR de langue.
 * Retombe sur l'accueil de la langue cible si la page n'a pas d'équivalent
 * (ex. /a-propos, /cookies, /reservation/succes n'ont pas de version EN).
 */
export const localeAlternatePath = (
  pathname: string,
  target: Locale,
): string => {
  const path = normalizePath(pathname);
  if (localeFromPath(path) === target) return path;

  const fr = toFrCanonical(path);
  if (target === "fr") return fr;

  if (target === "en") {
    if (FR_TO_EN[fr]) return FR_TO_EN[fr];
    for (const prefix of MIRROR_PREFIXES) {
      if (fr.startsWith(prefix)) return `/en${fr}`;
    }
    return "/en";
  }

  // target === "ar"
  if (FR_TO_AR[fr]) return FR_TO_AR[fr];
  for (const prefix of MIRROR_PREFIXES) {
    if (fr.startsWith(prefix)) return `/ar${fr}`;
  }
  return "/ar";
};

/**
 * Localise un href de navigation (les configs header/footer sont en FR) vers
 * `locale`. Conserve un éventuel fragment `#...`. Contrairement au switcher,
 * retombe sur le href FR d'origine quand il n'y a pas d'équivalent EN — un lien
 * de nav doit rester valide plutôt que de renvoyer vers l'accueil.
 */
export const localeNavHref = (frHref: string, locale: Locale): string => {
  if (locale === "fr") return frHref;
  const hashIndex = frHref.indexOf("#");
  const hash = hashIndex >= 0 ? frHref.slice(hashIndex) : "";
  const path = normalizePath(frHref);

  if (locale === "en") {
    if (FR_TO_EN[path]) return `${FR_TO_EN[path]}${hash}`;
    for (const prefix of MIRROR_PREFIXES) {
      if (path.startsWith(prefix)) return `/en${path}${hash}`;
    }
    // Pas d'équivalent EN (ex. /a-propos) → garder le lien FR valide.
    return `${path}${hash}`;
  }

  // locale === "ar"
  if (FR_TO_AR[path]) return `${FR_TO_AR[path]}${hash}`;
  for (const prefix of MIRROR_PREFIXES) {
    if (path.startsWith(prefix)) return `/ar${path}${hash}`;
  }
  return `${path}${hash}`;
};

/**
 * La locale a-t-elle une vraie page pour ce chemin ? Sert à GATER le switcher
 * (et le hreflang) pour l'arabe en cours de déploiement → jamais de 404.
 * fr/en : toujours (déployées). ar : seulement si jumeau explicite FR_TO_AR.
 */
export const localeHasPage = (pathname: string, locale: Locale): boolean => {
  if (FULLY_ROLLED_OUT_LOCALES.includes(locale)) return true;
  const fr = toFrCanonical(normalizePath(pathname));
  return locale === "ar"
    ? Boolean(FR_TO_AR[fr]) || MIRROR_PREFIXES.some((p) => fr.startsWith(p))
    : false;
};
